import { FileText, Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';

type ImportHistoryRecord = {
  id: string;
  importedAt: string | Date;
  importType: string;
  fileName: string;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  status: string;
  details: any;
  user: {
    email?: string | null;
    username?: string | null;
    fullName?: string | null;
  } | null;
};

interface ImportHistoryDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  record: ImportHistoryRecord | null;
}

export function ImportHistoryDetailDialog({
  open,
  onOpenChange,
  record,
}: ImportHistoryDetailDialogProps) {
  if (!record) return null;

  const details = record.details as any;
  const sheets = details?.sheets || {};
  const sheetNames = Object.keys(sheets);

  const exportToCSV = () => {
    // Simple CSV export of all rows across all sheets
    let csvContent = 'Sheet,Row,Status,Identifier,Message\n';
    for (const sheetName of sheetNames) {
      const sheetData = sheets[sheetName];
      const rows: any[] = sheetData?.rows || [];
      for (const r of rows) {
        const message = (r.reason || r.message || '').replace(/"/g, '""');
        csvContent += `"${sheetName}",${r.row},"${r.status}","${r.identifier || ''}","${message}"\n`;
      }
    }
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import-history-${record.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Import Details
          </DialogTitle>
          <DialogDescription>
            Full breakdown of import: {record.fileName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-muted rounded-md">
              <p className="text-xs text-muted-foreground">Imported By</p>
              <p className="text-sm font-medium">
                {record.user?.fullName || record.user?.username || record.user?.email || '—'}
              </p>
            </div>
            <div className="p-3 bg-muted rounded-md">
              <p className="text-xs text-muted-foreground">Date & Time</p>
              <p className="text-sm font-medium">{new Date(record.importedAt).toLocaleString()}</p>
            </div>
            <div className="p-3 bg-muted rounded-md">
              <p className="text-xs text-muted-foreground">Import Type</p>
              <Badge variant="outline" className="capitalize mt-1">
                {record.importType.replace(/_/g, ' ')}
              </Badge>
            </div>
            <div className="p-3 bg-muted rounded-md">
              <p className="text-xs text-muted-foreground">Overwrite Mode</p>
              <p className="text-sm font-medium">{details?.overwriteMode ? 'Enabled' : 'Disabled'}</p>
            </div>
          </div>

          {/* Counts */}
          <div className="grid grid-cols-4 gap-2">
            <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-center">
              <p className="text-xs text-muted-foreground">Created</p>
              <p className="text-lg font-bold text-green-600 dark:text-green-400">{record.createdCount}</p>
            </div>
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-center">
              <p className="text-xs text-muted-foreground">Updated</p>
              <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{record.updatedCount}</p>
            </div>
            <div className="p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-center">
              <p className="text-xs text-muted-foreground">Skipped</p>
              <p className="text-lg font-bold text-muted-foreground">{record.skippedCount}</p>
            </div>
            <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-center">
              <p className="text-xs text-muted-foreground">Errors</p>
              <p className="text-lg font-bold text-red-600 dark:text-red-400">{record.errorCount}</p>
            </div>
          </div>

          {details?.executionOrder && (
            <div className="p-3 bg-muted rounded-md">
              <p className="text-xs text-muted-foreground mb-1">Execution Order</p>
              <p className="text-sm font-mono">
                {details.executionOrder.map((s: string) => s.replace(/_/g, ' ')).join(' → ')}
              </p>
            </div>
          )}

          {/* Per-sheet breakdown */}
          {sheetNames.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium">Per-Sheet Breakdown</h4>
                <Button variant="outline" size="sm" onClick={exportToCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
              <Tabs defaultValue={sheetNames[0]} className="w-full">
                <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${Math.min(sheetNames.length, 4)}, 1fr)` }}>
                  {sheetNames.slice(0, 4).map((sheet) => (
                    <TabsTrigger key={sheet} value={sheet} className="capitalize text-xs">
                      {sheet.replace(/_/g, ' ')}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {sheetNames.map((sheet) => {
                  const sheetData = sheets[sheet];
                  const rows: any[] = sheetData?.rows || [];
                  return (
                    <TabsContent key={sheet} value={sheet} className="mt-3">
                      <div className="border rounded-md max-h-[300px] overflow-auto">
                        <table className="text-xs w-full">
                          <thead className="sticky top-0 bg-muted">
                            <tr className="border-b">
                              <th className="text-left py-2 px-3">Row</th>
                              <th className="text-left py-2 px-3">Status</th>
                              <th className="text-left py-2 px-3">Identifier</th>
                              <th className="text-left py-2 px-3">Message</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r: any, i: number) => (
                              <tr key={i} className="border-b last:border-0 hover:bg-muted/50">
                                <td className="py-2 px-3">{r.row}</td>
                                <td className="py-2 px-3">
                                  <Badge
                                    variant={
                                      r.status === 'created' ? 'default' :
                                      r.status === 'updated' ? 'default' :
                                      r.status === 'error' ? 'destructive' :
                                      'outline'
                                    }
                                    className={
                                      r.status === 'created' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                      r.status === 'updated' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                      ''
                                    }
                                  >
                                    {r.status}
                                  </Badge>
                                </td>
                                <td className="py-2 px-3 font-mono">{r.identifier || '—'}</td>
                                <td className="py-2 px-3 text-muted-foreground">{r.reason || r.message || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {rows.length} {rows.length === 1 ? 'row' : 'rows'} in this sheet
                      </p>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}



